import React from "react";

/**
 * Top-level error boundary so a single render error in any component does
 * not whitescreen the whole app.  Shows a minimal fallback with a reload CTA.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Unhandled render error:", error, info);
    this.setState({ info });
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, info: null });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, info } = this.state;
    const detail = error?.message || String(error || "");
    const stack = error?.stack || "";
    const componentStack = info?.componentStack || "";

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          background: "#fafafa",
          color: "#1f2937",
        }}
      >
        <div style={{ maxWidth: 720, textAlign: "center" }}>
          <h1 style={{ fontSize: 22, marginBottom: 12 }}>Something went wrong</h1>
          <p style={{ fontSize: 14, color: "#4b5563", marginBottom: 20 }}>
            The app hit an unexpected error. You can reload to recover. If this keeps
            happening, please contact support.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: "10px 18px",
              borderRadius: 8,
              border: "none",
              background: "#2563eb",
              color: "#fff",
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Reload app
          </button>
          {detail && (
            <details
              style={{
                marginTop: 24,
                textAlign: "left",
                fontSize: 12,
                color: "#6b7280",
                background: "#f3f4f6",
                border: "1px solid #e5e7eb",
                borderRadius: 6,
                padding: "12px 14px",
              }}
            >
              <summary style={{ cursor: "pointer", fontWeight: 600, color: "#374151" }}>
                Error details
              </summary>
              <pre
                style={{
                  marginTop: 8,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  maxHeight: 240,
                  overflow: "auto",
                }}
              >
                {detail}
                {stack ? "\n\n" + stack : ""}
                {componentStack ? "\n\nComponent stack:" + componentStack : ""}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
